import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import React, { useEffect, useReducer, useState } from 'react';
import {
  useFindAllCompaniesQuery,
  useFindAllHardwareQuery,
  useFindAllUsersQuery,
  useFindOneSignupQuery,
  useRemoveSignupMutation,
} from '../../api/api-hooks';
import { UserNoPassword } from '../../api/api-types';
import { prettyPrintItem } from '../../utils/stringManipulation';
import { UserForm } from './userTypes';
import { userValidator } from './userUtils';
import useUserItemCreatorWithPassword from './useUserItemCreatorWithPassword';

interface Props {
  dialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  signupId: string | number;
}

const addFormInitialState: UserForm = {
  name: undefined,
  email: undefined,
  phone: undefined,
  address: undefined,
  company: null,
};

const SignupTransferDialog = ({
  dialogOpen,
  setDialogOpen,
  signupId,
}: Props) => {
  const { data: companies } = useFindAllCompaniesQuery({});
  const { data: analyzers } = useFindAllHardwareQuery({});
  const { data: users } = useFindAllUsersQuery({});
  const [matchingUser, setMatchingUser] = useState<UserNoPassword | undefined>(
    undefined
  );
  const [deleteSignup] = useRemoveSignupMutation();
  const userCreator = useUserItemCreatorWithPassword();
  const [result, setResult] = useState<string>('');
  const { data: signup } = useFindOneSignupQuery({
    id: Number(signupId),
  });
  const [addFormState, setAddFormState] = useReducer(
    (curVals: any, newVals: any) => ({ ...curVals, ...newVals }),
    addFormInitialState
  );
  // Refresh form initial state whenver signupId or the pulled Signup changes
  useEffect(() => {
    if (signup === undefined || !signupId) return;
    setAddFormState({
      ...addFormInitialState,
      name: signup.name,
      email: signup.email,
      phone: signup.phone,
      address: signup.address,
    });
  }, [signupId, signup]);

  // Refresh matching user when form state changes
  useEffect(() => {
    if (users === undefined) return;
    setMatchingUser(users.find((user) => user.email === addFormState.email));
  }, [addFormState, users]);

  if (
    companies === undefined ||
    analyzers === undefined ||
    signup === undefined
  )
    return null;

  const matchingAnalyzer = analyzers
    .filter((analyzer) => {
      if (!addFormState.company) return false;
      const analyzerCompanyIds = analyzer.companies.map(
        (company) => company.id
      );
      return analyzerCompanyIds.includes(addFormState.company.id);
    })
    .find((analyzer) => analyzer.serial === signup.analyzerSerial);
  const handleFormChange = (event: any, autoboxValue?: object) => {
    const { name, value } = event.target;
    if (autoboxValue) {
      setAddFormState({
        [name]: value,
        company: autoboxValue,
      });
    } else {
      setAddFormState({
        [name]: value,
      });
    }
  };
  const handleSubmit = async () => {
    const createResult = await userCreator(addFormState, signup.password);
    if (!createResult) setResult('Failed to create user.');
    else if ('error' in createResult)
      // @ts-ignore
      setResult(`Failed to create user. ${createResult.error.data.message}`);
    else {
      deleteSignup({ id: Number(signupId) });
      setResult('User created and signup removed.');
    }
  };
  const handleCancel = () => {
    setDialogOpen(false);
    setResult('');
    setAddFormState({ company: null });
  };
  const handleDelete = () => {
    deleteSignup({ id: Number(signupId) });
    setResult('Signup deleted.');
  };
  return (
    <Dialog
      fullWidth
      maxWidth='md'
      open={dialogOpen}
      aria-labelledby='form-dialog-title'
    >
      <DialogTitle id='form-dialog-title'>Process Signup</DialogTitle>
      {!result ? (
        <DialogContent>
          <Grid container direction='column' spacing={1}>
            <Grid item>
              <Autocomplete
                onChange={handleFormChange}
                value={addFormState.company}
                options={companies}
                getOptionLabel={(company) => company.companyName}
                style={{ width: 300, marginTop: '.5rem' }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Select Company'
                    variant='outlined'
                  />
                )}
              />
            </Grid>
            <Grid item>
              <Typography>User's Provided Company: {signup.company}</Typography>
            </Grid>
            <Grid item>
              <TextField
                margin='dense'
                name='name'
                label='Name'
                fullWidth
                value={addFormState.name}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item>
              <TextField
                margin='dense'
                name='email'
                label='Email Address'
                fullWidth
                value={addFormState.email}
                onChange={handleFormChange}
              />
            </Grid>
            {matchingUser && (
              <Grid item>
                <Typography color='secondary'>
                  User email already in use.
                </Typography>
              </Grid>
            )}
            <Grid item>
              <TextField
                margin='dense'
                name='phone'
                label='Phone'
                fullWidth
                value={addFormState.phone}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item>
              <TextField
                margin='dense'
                name='address'
                label='Address'
                fullWidth
                value={addFormState.address}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item>
              <Typography>
                User's Provided Hardware Serial: {signup.analyzerSerial}
              </Typography>
            </Grid>
            <Grid item>
              {matchingAnalyzer ? (
                <>
                  <Typography>
                    Serial matches the following company hardware:
                  </Typography>
                  <Typography>{prettyPrintItem(matchingAnalyzer)}</Typography>
                </>
              ) : addFormState.company ? (
                <Typography color='error'>
                  Serial does not match hardware from the selected company.
                </Typography>
              ) : (
                <Typography color='error'>
                  Serial does not match any company's hardware.
                </Typography>
              )}
            </Grid>
            <Grid item>
              <Autocomplete
                options={analyzers}
                getOptionLabel={(analyzer) => analyzer.serial}
                style={{ width: 300 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Manual Analyzer Search'
                    variant='outlined'
                  />
                )}
              />
            </Grid>
            {!userValidator(addFormState) && (
              <Grid item>
                <Typography color='secondary'>
                  To create a user, Name, Email, and Company are required.
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
      ) : (
        <DialogContent>
          <Typography>{result}</Typography>
        </DialogContent>
      )}
      {result ? (
        <DialogActions>
          <Button onClick={handleCancel} color='primary'>
            Close
          </Button>
        </DialogActions>
      ) : (
        <DialogActions>
          <Button onClick={handleCancel} color='primary'>
            Cancel
          </Button>
          <Button onClick={handleDelete} color='secondary'>
            Delete Signup
          </Button>
          {userValidator(addFormState) && !matchingUser && (
            <Button onClick={handleSubmit} color='primary'>
              Create User
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SignupTransferDialog;
